import { Link } from "react-router-dom";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { profitabilityRulePath } from "@/profitability/routes";
import { useTemplateSettings } from "@/profitability/settings/query";
import { EditRuleButton } from "@/profitability/template-page/edit-rule-button";
import type { Finding } from "./types";

const useFindingRules = (businessId: string, finding: Finding) => {
  const settings = useTemplateSettings(businessId, finding.report_id);
  return {
    editable: settings.data?.editable,
    rules: (settings.data?.rules ?? []).filter((rule) =>
      finding.rule_ids?.includes(rule.id),
    ),
  };
};

export const FindingRuleLinks = ({
  businessId,
  finding,
}: {
  businessId: string;
  finding: Finding;
}) => {
  const { rules } = useFindingRules(businessId, finding);
  const ruleIds = finding.rule_ids ?? [];
  return (
    <Stack direction="row" gap={2} wrap className="min-w-0">
      {ruleIds.length ? (
        ruleIds.map((id, index) => {
          const rule = rules.find((item) => item.id === id);
          const label =
            rule?.label ??
            (index === 0 ? finding.rule : id.replaceAll("_", " "));
          return (
            <Link
              key={id}
              to={profitabilityRulePath(finding.report_id, id, finding.run_id)}
              className="text-sm font-medium text-pretty [overflow-wrap:anywhere] underline decoration-line underline-offset-4 hover:decoration-ink"
            >
              {label}
            </Link>
          );
        })
      ) : (
        <Text variant="label" className="text-pretty">
          {finding.rule}
        </Text>
      )}
    </Stack>
  );
};

export const FindingRuleActions = ({
  businessId,
  finding,
}: {
  businessId: string;
  finding: Finding;
}) => {
  const { rules, editable } = useFindingRules(businessId, finding);
  if (!editable || !rules.length) return null;
  return (
    <Stack gap={2} align="start">
      {rules.map((rule) => (
        <Stack key={rule.id} gap={1} align="start">
          {rules.length > 1 && (
            <Text variant="caption" tone="muted">
              {rule.label}
            </Text>
          )}
          <EditRuleButton
            reportId={finding.report_id}
            rule={rule}
            finding={finding}
          />
        </Stack>
      ))}
    </Stack>
  );
};
